'use client';

import classNames from 'classnames';
import { useParams } from 'next/navigation';
import { FC, KeyboardEvent, useEffect, useState } from 'react';
import type { Labels } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import ar from 'react-phone-number-input/locale/ar.json';
import en from 'react-phone-number-input/locale/en.json';
import es from 'react-phone-number-input/locale/es.json';
import fr from 'react-phone-number-input/locale/fr.json';
import pt from 'react-phone-number-input/locale/pt.json';
import ru from 'react-phone-number-input/locale/ru.json';
import zh from 'react-phone-number-input/locale/zh.json';
import PhoneNumberInput, {
  Country,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
} from 'react-phone-number-input/max';

import CountrySelect from './CountrySelect';

import 'react-phone-number-input/style.css';
import './styles.scss';

const LABELS: Record<string, Labels> = { en, es, zh, ru, ar, fr, pt };

const LANG_COUNTRY: Record<string, Country> = {
  en: 'US',
  es: 'ES',
  zh: 'CN',
  ru: 'RU',
  hi: 'IN',
  ar: 'AE',
  fr: 'FR',
  pt: 'BR',
};

const CALLING_CODES = new Set(getCountries().map((country) => `+${getCountryCallingCode(country)}`));

const normalize = (raw?: string) => (raw && !CALLING_CODES.has(raw) ? raw : '');

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (country?: Country) => void;
  name?: string;
  placeholder?: string;
  className?: string;
  hasError?: boolean;
}

const PhoneInput: FC<PhoneInputProps> = ({
  value,
  onChange,
  onCountryChange,
  name = 'phone',
  placeholder,
  className,
  hasError,
}) => {
  const { lang } = useParams<{ lang: string }>();
  const [rawValue, setRawValue] = useState<string | undefined>(value || undefined);

  useEffect(() => {
    if (value !== normalize(rawValue)) {
      setRawValue(value || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (next?: string) => {
    setRawValue(next);
    onChange(normalize(next));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd, value: text } = event.currentTarget;
    const isCaretAtEnd = selectionStart === text.length && selectionEnd === text.length;

    if (
      /^\d$/.test(event.key) &&
      isCaretAtEnd &&
      value &&
      isValidPhoneNumber(value) &&
      !isValidPhoneNumber(value + event.key)
    ) {
      event.preventDefault();
    }
  };

  return (
    <PhoneNumberInput
      name={name}
      value={rawValue}
      onChange={handleChange}
      onCountryChange={onCountryChange}
      onKeyDown={handleKeyDown}
      defaultCountry={LANG_COUNTRY[lang] ?? 'US'}
      labels={LABELS[lang] ?? en}
      placeholder={placeholder}
      autoComplete="tel"
      international
      countryCallingCodeEditable={false}
      limitMaxLength
      addInternationalOption={false}
      focusInputOnCountrySelection
      countrySelectComponent={CountrySelect}
      flags={flags}
      className={classNames('popOkPhoneInput', className, { errorState: hasError })}
      containerComponentProps={{ dir: 'ltr' }}
    />
  );
};

export default PhoneInput;
